import axios, {
  AxiosRequestConfig,
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { VITE_ENV } from 'config';
import {
  externalLogout,
  getAuthorizationHeader,
  getBearerTokenFromHeader,
  isRefreshRequest,
  isAuthRequest,
  isTokenExpirationValid,
  refreshToken,
  isLogoutInProgress,
} from 'identityAndAccess/infrastructure/utils';
import loggerService, { isLoggingRequest, LoggerLevel } from 'common/infrastructure/log';
import { JwtClaimVerificationService } from 'identityAndAccess/infrastructure/jwtClaimVerification';

interface Config extends AxiosRequestConfig {
  tpMobileRetryAttemptCount?: number;
}

export interface HttpErrorResponse {
  status: number;
  data: unknown;
  message: string;
}

let refreshing = false;
const errorTypeHeader = 'x-amzn-errortype';
const unauthorizedErrorValues = ['UnauthorizedException', 'Unauthorized', 'ERR_UNAUTHORIZED'];
const WORK_MANAGEMENT_PATH_PREFIX = '/work-management';

/**
 * Determines if the request is for the work management API
 * @param url string
 * @returns boolean
 */
const isWorkManagementRequest = (url: string): boolean => url.includes(WORK_MANAGEMENT_PATH_PREFIX);

/**
 * Interceptor to resolve the appropriate base URL for each API
 * @param config InternalAxiosRequestConfig
 * @returns config (Promise<InternalAxiosRequestConfig>)
 */
const baseUrlResolverInterceptor = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  // If URL is already absolute, don't modify it
  if (config.url?.startsWith('http')) {
    return config;
  }

  // Determine which base URL to use based on path
  const url = config.url || '';
  if (isWorkManagementRequest(url)) {
    // Strip the work management prefix since the base URL already points to the work management API
    const pathWithoutPrefix = url.replace(WORK_MANAGEMENT_PATH_PREFIX, '');
    config.url = `${VITE_ENV.workManagementUrl}${pathWithoutPrefix}`;
  } else {
    // Default to tp3 API
    config.url = `${VITE_ENV.tp3Url}${url}`;
  }

  return config;
};

/**
 * Interceptor to add Authorization header to requests
 * All APIs (tp3, work-management, and future APIs) use the same authentication method
 * @param config InternalAxiosRequestConfig
 * @returns config (Promise<InternalAxiosRequestConfig>)
 */
const authorizationInterceptor = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';

  config.headers = config.headers ?? {};
  config.headers.Authorization = 'Bearer ' + idToken;

  const cognitoAuthStrategyEndpoints = [
    '/command-service/tenant/service/tp2-interop/put-tp2-field-config',
  ];

  const url = config.url || '';
  const isURLAuthStrategyForCognito = cognitoAuthStrategyEndpoints.some((endpoint) =>
    url.includes(endpoint)
  );

  if (isURLAuthStrategyForCognito) {
    config.headers['X-Auth-Strategy'] = 'Cognito';
  }

  return { ...config };
};

/**
 * Interceptor to refresh token if expired
 * @param request InternalAxiosRequestConfig
 * @returns request Promise<InternalAxiosRequestConfig>
 */
const refreshTokenInterceptor = async (
  request: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  if (isRefreshRequest(request.url || '', request.method || '')) {
    return request;
  }
  await refreshTokenIfExpired(request);

  return request;
};

/**
 * Interceptor to handle successful responses
 * @param response any
 * @returns any
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const responseInterceptor = (response: any) => {
  if (isAuthRequest(response.config.url || '') || isLoggingRequest(response.config.url || '')) {
    return response;
  }
  // Orval stores this response in the React Query query. This query is saved to local storage,
  // which doesn't support functions on the response object.
  return response.data;
};

/**
 * Interceptor to handle errors
 * @param error Error | AxiosError
 * @returns Promise<any>
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorInterceptor = async (error: Error | AxiosError): Promise<any> => {
  if (!axios.isAxiosError(error)) {
    const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';
    const tokenValid = idToken ? isTokenExpirationValid(idToken) : false;
    loggerService.log(
      LoggerLevel.DEBUG,
      '[LOGOUT_TRIGGER] Non-Axios error in HTTP client - triggering logout',
      {
        errorType: error.constructor.name,
        errorMessage: error.message,
        errorStack: error.stack,
        hasToken: !!idToken,
        tokenValid,
        url: (error as AxiosError)?.config?.url,
        method: (error as AxiosError)?.config?.method,
      }
    );
    await externalLogout({
      skipRefreshTokenRevocation: true,
      skipLogoutWhenOffline: true,
    });
    return Promise.reject('Something went wrong');
  }

  const { response } = error;
  const config = error.config as Config;

  // Handle 403 Forbidden errors - check JWT claims and handle tenant access
  if (response?.status === 403) {
    loggerService.log(
      LoggerLevel.WARN,
      '403 Forbidden error detected - triggering JWT claim verification and tenant switching'
    );
    await JwtClaimVerificationService.handle403Error();
    return Promise.reject('Access forbidden - tenant access updated');
  }

  if (isRefreshRequest(config?.url || '', config?.method || '')) {
    if (response?.status === 401) {
      // Token refresh request failed, likely due to an expired refresh token
      const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';
      loggerService.log(
        LoggerLevel.DEBUG,
        '[LOGOUT_TRIGGER] 401 on token refresh request - triggering logout',
        {
          url: config?.url,
          status: response?.status,
          statusText: response?.statusText,
          errorData: response?.data,
          hasToken: !!idToken,
        }
      );
      await externalLogout({
        skipRefreshTokenRevocation: true,
        skipLogoutWhenOffline: true,
      });
      return;
    }
  }

  if (
    isResponseOk(response) &&
    error.config &&
    isFirstAttempt(config?.tpMobileRetryAttemptCount) &&
    isUnauthorizedError(response?.status as number, responseErrorInfo(response)) &&
    !isTokenRefreshing() &&
    !isLogoutInProgress()
  ) {
    refreshing = true;
    try {
      const idToken = await refreshToken();
      if (idToken) {
        return await retryRequestWithNewToken(idToken, error.config);
      }
      throw error;
    } finally {
      refreshing = false;
    }
  }

  // Include HTTP status code in error message for better error handling
  const statusCode = error.response?.status || -1;
  const errorData = error.response?.data;

  if (errorData) {
    const errorObject: HttpErrorResponse = {
      status: statusCode,
      data: errorData,
      message: errorData.message || errorData.error || `HTTP ${statusCode} error`,
    };
    return Promise.reject(errorObject);
  } else {
    // Fallback to generic message with status code if available
    const errorMessage = statusCode
      ? `HTTP ${statusCode}: Something went wrong`
      : 'Something went wrong';
    return Promise.reject(errorMessage);
  }
};

/**
 * Creates an Axios instance with custom interceptors
 * @returns AxiosInstance
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    // Base URL is now determined dynamically by the baseUrlResolverInterceptor
    // based on the request path (work-management vs tp3)
    paramsSerializer: {
      serialize: (params) => {
        // Custom serializer to handle array parameters with explode: false (comma-separated)
        // This is required for OpenAPI specs that specify style: form, explode: false
        const parts: string[] = [];
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            // Convert array to comma-separated string (explode: false)
            parts.push(`${encodeURIComponent(key)}=${value.map(encodeURIComponent).join(',')}`);
          } else if (value !== undefined && value !== null) {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
          }
        });
        return parts.join('&');
      },
    },
  });

  // Order matters: base URL must be resolved before auth and token refresh
  instance.interceptors.request.use(baseUrlResolverInterceptor);
  instance.interceptors.request.use(authorizationInterceptor);
  instance.interceptors.request.use(refreshTokenInterceptor);

  instance.interceptors.response.use(responseInterceptor, errorInterceptor);

  return instance;
};

/**
 * Retries request with new token
 * @param idToken string
 * @param config Config
 * @returns Promise<any>
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const retryRequestWithNewToken = async (idToken: string, config: Config): Promise<any> => {
  // Axios issue:
  // Internally, Axios config.headers is mutated into an AxiosHeaders instance,
  // but the request method only accepts a plain headers object.
  // https://github.com/axios/axios/issues/5143
  config.headers = { ...config.headers };
  config.headers.Authorization = getAuthorizationHeader(idToken);
  config.tpMobileRetryAttemptCount = 1;

  return new Promise((resolve) => {
    resolve(axiosInstance(config));
  });
};

/**
 * Checks if token is actively refreshing
 * @returns boolean
 */
const isTokenRefreshing = (): boolean => refreshing;

/**
 * Attempts to refresh the token if it is expired.
 * Intended to be called when the app transitions from offline to online.
 * Resets any stuck refreshing state and proactively refreshes if needed.
 */
export const attemptTokenRefreshOnReconnect = async (): Promise<void> => {
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';
  if (!idToken || isLogoutInProgress()) {
    return;
  }

  refreshing = false;

  if (!isTokenExpirationValid(idToken)) {
    loggerService.log(
      LoggerLevel.DEBUG,
      '[TOKEN_REFRESH] Online reconnect detected with expired token, attempting refresh'
    );
    refreshing = true;
    try {
      await refreshToken(idToken);
    } finally {
      refreshing = false;
    }
  }
};

/**
 * Checks if response is valid
 * @param response any
 * @returns boolean
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isResponseOk = (response: any): boolean =>
  response?.status && response?.headers && !!responseErrorInfo(response);

/**
 * Extracts error information from response
 * @param response any
 * @returns string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const responseErrorInfo = (response: any): string =>
  response.headers[errorTypeHeader] || response?.statusText || response?.data?.errCode;

/**
 * Checks if error is unauthorized
 * @param status number
 * @param errorValue string
 * @returns boolean
 */
const isUnauthorizedError = (status: number, errorValue: string): boolean =>
  status === 401 && unauthorizedErrorValues.indexOf(errorValue) > -1;

/**
 * Checks if it's the first retry attempt
 * @param tpMobileRetryAttemptCount number | undefined
 * @returns boolean
 */
const isFirstAttempt = (tpMobileRetryAttemptCount: number | undefined): boolean => {
  if (tpMobileRetryAttemptCount === undefined) {
    return true;
  }

  return tpMobileRetryAttemptCount < 1;
};

/**
 * Refreshes token if expired
 * @param request AxiosRequestConfig
 * @returns Promise<void>
 */
const refreshTokenIfExpired = async (request: AxiosRequestConfig): Promise<void> => {
  if (request?.headers?.Authorization && !isLogoutInProgress()) {
    const idToken = getBearerTokenFromHeader(request.headers.Authorization as string);

    if (idToken && !isTokenExpirationValid(idToken) && !isTokenRefreshing()) {
      loggerService.log(
        LoggerLevel.DEBUG,
        '[TOKEN_REFRESH] Token expired, attempting refresh before request',
        {
          url: request.url,
          method: request.method,
          tokenExpired: !isTokenExpirationValid(idToken),
        }
      );
      refreshing = true;
      try {
        const token = await refreshToken(idToken);
        if (token) {
          loggerService.log(LoggerLevel.DEBUG, '[TOKEN_REFRESH] Token refresh successful');
        } else {
          loggerService.log(
            LoggerLevel.DEBUG,
            '[TOKEN_REFRESH] Token refresh returned empty token'
          );
        }
      } finally {
        refreshing = false;
      }
    }
  }
};

export const axiosInstance = createAxiosInstance();

// create client for reactQuery/orval
// Resolves to the response body, not an AxiosResponse: responseInterceptor unwraps
// response.data for every non-auth, non-logging request. Since axios 1.20 the instance's
// return type is a conditional (AxiosResponseResult) that TypeScript cannot narrow to T
// for a generic T, so the unwrapped type has to be asserted here.
export const httpClient = <T>(config: AxiosRequestConfig): Promise<T> =>
  axiosInstance({ ...config }) as unknown as Promise<T>;
